if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
        // When true, updateOptionValues will NOT attempt to restore the
        // client's previous selection after replacing the variant-selects
        // HTML. This is used during initial hydration when the server may
        // choose a different default variant.
        this._suppressVariantSelectorRestore = false;
        // When true, prevent the client from updating the browser URL
        // during the initial server-rendered hydration fetch.
        this._suppressInitialUrlUpdate = false;
        // When true, the next server fragment fetch should NOT replace the
        // variant-selects HTML; instead we'll try to merge updated variant
        // data into the existing selectors so the user's explicit clicks
        // are not overwritten by the server render.
        this._suppressVariantSelectorRestoreForNextFetch = false;
        // Keep a copy of the last requested option value ids for the
        // pending fetch so updateOptionValues can make merge decisions.
        this._lastRequestedOptionValues = null;
        // When true, suppress URL updates during the current option-change
        // flow so the browser history is not affected by variant query params.
        this._suppressUrlUpdateForEvent = false;
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true }),
        );
        this.showDiscountedPriceFromBodyTag();

        // Ensure first paint respects the server-selected variant without touching the URL
        try {
          const initialVariant = this.getSelectedVariant(this);
          const initialVariantId =
            initialVariant?.id ||
            (this.dataset.initialVariantId
              ? parseInt(this.dataset.initialVariantId, 10)
              : null);

          // Prefer an explicit ?variant= query param on initial load. If
          // present, it should override the server-selected default so the
          // page reflects the URL requested variant on first paint.
          let urlVariantId = null;
          try {
            const qp = new URLSearchParams(window.location.search);
            const v = qp.get("variant");
            if (v) urlVariantId = parseInt(v, 10);
          } catch (e) {
            /* ignore malformed URL */
          }

          const resolvedInitialVariantId = urlVariantId ?? initialVariantId;

          if (resolvedInitialVariantId) {
            // Keep hidden form inputs in sync in case of dynamic hydration
            this.updateVariantInputs(resolvedInitialVariantId);

            // Ensure the DOM selection reflects the resolved variant id so
            // selects/radios show the requested variant on first paint.
            try {
              const vs = this.variantSelectors;
              if (vs) {
                // Find any element that carries data-variant-id for this variant
                const sel = vs.querySelector(
                  `[data-variant-id="${resolvedInitialVariantId}"]`,
                );
                if (sel) {
                  if (sel.tagName === "OPTION") {
                    const parent = sel.closest("select");
                    if (parent) {
                      parent.value = sel.value;
                      Array.from(parent.options).forEach((o) =>
                        o.removeAttribute("selected"),
                      );
                      sel.setAttribute("selected", "selected");
                      vs.updateSelectionMetadata({ target: sel });
                    }
                  } else if (sel.tagName === "INPUT" && sel.type === "radio") {
                    const name = sel.getAttribute("name");
                    Array.from(
                      vs.querySelectorAll(`input[name="${name}"]`),
                    ).forEach((r) => (r.checked = false));
                    sel.checked = true;
                    vs.updateSelectionMetadata({ target: sel });
                  }
                }
              }
            } catch (e) {
              /* best-effort DOM sync */
            }

            // Ensure collapsible tabs reflect the resolved variant
            this.updateCollapsibleTabs(resolvedInitialVariantId);

            // Update pickup availability widget using the resolved variant
            let resolvedVariantObject = null;
            try {
              if (
                initialVariant &&
                initialVariant.id === resolvedInitialVariantId
              ) {
                resolvedVariantObject = initialVariant;
              } else {
                const all = this.getAllVariants() || [];
                resolvedVariantObject =
                  all.find((v) => v.id === resolvedInitialVariantId) || null;
              }
            } catch (e) {
              resolvedVariantObject = null;
            }

            if (resolvedVariantObject)
              this.pickupAvailability?.update(resolvedVariantObject);

            // Fetch the server-rendered section fragment for this variant so
            // media (gallery) and price blocks are updated to match the
            // server-selected variant. We reuse the existing renderProductInfo
            // + handleUpdateProductInfo flow to keep update logic centralized.
            try {
              const requestUrl = `${this.dataset.url}?variant=${resolvedInitialVariantId}&section_id=${this.sectionId}`;
              // Prevent the client from re-applying its previous option
              // selection after we fetch the server-rendered fragment for
              // the initial variant. When a `?variant=` query param was
              // provided we should prefer the URL value, so only suppress
              // the client's selection when no explicit URL variant exists.
              if (!urlVariantId) this._suppressVariantSelectorRestore = true;
              // Also avoid updating the URL during this initial hydration
              // flow; the server-rendered page should not modify the
              // browser's query string on first paint.
              this._suppressInitialUrlUpdate = true;
              this.renderProductInfo({
                requestUrl,
                callback: this.handleUpdateProductInfo(this.dataset.url),
              });
            } catch (e) {
              /* non-fatal; leave initial render as-is */
            }
          }
        } catch (e) {
          // Non-fatal: proceed with existing server-rendered state
          console.warn("product-info: initial variant sync skipped", e);
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel"),
            ),
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      // --------------------------------------------------
      // Handle option change
      // --------------------------------------------------
      handleOptionValueChange({ data }) {
        const {
          event,
          target,
          selectedOptionValues = [],
          suppressVariantSelectorRestore,
        } = data || {};

        // Guard Clauses
        if (!this._isValidEvent(event)) return;
        if (!this.contains(event.target)) return;

        // Setup + Suppression State
        if (suppressVariantSelectorRestore) {
          this._enableEventSuppression();
        }

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        // Resolve Selection State
        const selectionContext = this._buildSelectionContext({
          target,
          selectedOptionValues,
        });

        if (this._hasMissingSelections(selectionContext.selectedByPosition)) {
          this._performLocalIncompleteUpdate();
          return;
        }

        // Optimistic URL Update
        this._syncUrlOptimistically(productUrl, selectionContext, event);

        // Suppression Handling
        if (this._handleSuppressionFlow(selectionContext, productUrl)) {
          return;
        }

        // Local Variant Resolution
        const localMatch = this._findLocalVariant(selectionContext);

        if (localMatch) {
          const handled = this._applyLocalVariant(localMatch, selectionContext);
          if (handled) return;
        }

        // Server Fetch Fallback
        this._fetchServerFragment({
          productUrl,
          selectionContext,
          shouldSwapProduct,
          shouldFetchFullPage,
          target,
          event,
        });
      }

      _isValidEvent(event) {
        if (!event) return false;
        return true;
      }

      _enableEventSuppression() {
        // Suppression triggered by user action.
        // Prevents server from restoring default sibling options.
        this._suppressVariantSelectorRestore = true;
        this._suppressVariantSelectorRestoreFromEvent = true;
      }

      _buildSelectionContext({ target, selectedOptionValues }) {
        const context = {
          selectedByPosition: {},
          requestOptionValues: Array.isArray(selectedOptionValues)
            ? [...selectedOptionValues]
            : [],
          changedPosition: null,
          changedValue: null,
        };

        // Read selected options from DOM
        if (this.variantSelectors) {
          context.selectedByPosition = this.getSelectedOptionsByPosition(
            this.variantSelectors,
          );
        }

        // Ensure clicked value is reflected
        context.changedPosition = parseInt(
          target?.dataset?.optionPosition || "",
          10,
        );

        context.changedValue = (target?.value || target?.textContent || "")
          .toString()
          .trim();

        if (context.changedPosition && context.changedValue) {
          context.selectedByPosition[context.changedPosition] =
            context.changedValue;
        }

        return context;
      }

      _isSelectionComplete(selectedByPosition = {}) {
        const requiredPositions = new Set();

        try {
          this.variantSelectors
            ?.querySelectorAll?.("[data-option-position]")
            ?.forEach((group) => {
              const pos = parseInt(group.dataset.optionPosition || "", 10);
              if (!Number.isNaN(pos) && pos > 0) requiredPositions.add(pos);
            });
        } catch (e) {
          /* best-effort */
        }

        if (!requiredPositions.size) {
          const variants = this.getAllVariants() || [];
          const first = variants[0] || null;
          if (first) {
            [first.option1, first.option2, first.option3].forEach(
              (value, index) => {
                if (value !== undefined && value !== null && value !== "") {
                  requiredPositions.add(index + 1);
                }
              },
            );
          }
        }

        if (!requiredPositions.size) return false;

        return Array.from(requiredPositions).every((position) => {
          const value = selectedByPosition[position];
          return String(value || "").trim() !== "";
        });
      }

      _syncUrlOptimistically(productUrl, context, event) {
        const variants = this.getAllVariants();
        if (!variants || !Object.keys(context.selectedByPosition).length)
          return;

        //if (!this._isSelectionComplete(context.selectedByPosition)) return;

        const match = this.findVariantBySelectedOptions(
          variants,
          context.selectedByPosition,
        );

        if (!match?.id) return;

        // Keep current optimistic URL behavior compatible with existing
        // tests/callers while still allowing later flows to suppress
        // history updates when needed.
        if (event) this._suppressUrlUpdateForEvent = true;
        this.updateURL(productUrl, match.id);
      }

      _handleSuppressionFlow(selection, productUrl) {
        if (!this._suppressVariantSelectorRestore) return false;

        if (this._suppressVariantSelectorRestoreFromEvent) {
          if (this._hasMissingSelections(selection.selectedByPosition)) {
            this._performLocalIncompleteUpdate();
            this._clearSuppressionFlags();
            return true;
          }

          // If a valid variant exists after event-driven selector adjustments
          // (e.g., auto-selecting a compatible top option), continue normal
          // flow so UI/data updates for that variant are applied.
          const eventMatch = this._findLocalVariant(selection);
          if (eventMatch) {
            this._clearSuppressionFlags();
            return false;
          }

          this._performLocalIncompleteUpdate();
          this._clearSuppressionFlags();
          return true;
        }

        const match = this._findLocalVariant(selection);

        if (!match) {
          this._performLocalIncompleteUpdate();
          this._clearSuppressionFlags();
          return true;
        }

        this._clearSuppressionFlags();
        return false;
      }

      _clearSuppressionFlags() {
        this._suppressVariantSelectorRestore = false;
        this._suppressVariantSelectorRestoreFromEvent = false;
      }

      _findLocalVariant(selection) {
        const variants = this.getAllVariants();
        if (!variants) return null;

        return this.findVariantBySelectedOptions(
          variants,
          selection.selectedByPosition,
        );
      }

      _applyLocalVariant(variant, context) {
        if (variant.available) {
          this._applyAvailableVariant(variant);
          return true;
        }

        this._applyOutOfStockState(variant, context);
        return true;
      }

      _applyOutOfStockState(variant, selection) {
        this.updateVariantInputs(null);
        this.variantSelectors?.recalculateAvailability?.();

        this.productForm?.toggleSubmitButton(
          true,
          this.getMissingOptionPromptText(),
        );

        publish(PUB_SUB_EVENTS.variantChange, {
          data: { sectionId: this.sectionId, html: null, variant: null },
        });
      }

      _applyAvailableVariant(variant) {
        this.updateVariantInputs(variant.id);
        this.variantSelectors?.recalculateAvailability?.();
        this.productForm?.toggleSubmitButton(false);

        // Update media
        const mediaId = variant.featured_media?.id;
        if (mediaId) {
          this.querySelector("media-gallery")?.setActiveMedia?.(
            `${this.dataset.section}-${mediaId}`,
            true,
          );
        }

        publish(PUB_SUB_EVENTS.variantChange, {
          data: { sectionId: this.sectionId, html: null, variant },
        });

        // Fetch merged server fragment (without replacing selectors)
        this._suppressVariantSelectorRestoreForNextFetch = true;
        this._suppressUrlUpdateForEvent = true;

        const requestUrl = `${this.dataset.url}?variant=${variant.id}&section_id=${this.sectionId}`;

        this.renderProductInfo({
          requestUrl,
          callback: this.handleUpdateProductInfo(this.dataset.url),
        });
      }

      _fetchServerFragment({
        productUrl,
        selectionContext,
        shouldSwapProduct,
        shouldFetchFullPage,
        target,
        event,
      }) {
        const requestUrl = this.buildRequestUrlWithParams(
          productUrl,
          selectionContext.requestOptionValues,
          shouldFetchFullPage,
          selectionContext.selectedByPosition,
        );

        this.renderProductInfo({
          requestUrl,
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });

        // Bookkeeping for selector merge behavior
        this._lastRequestedOptionValues = [
          ...selectionContext.requestOptionValues,
        ];

        if (event) {
          this._suppressVariantSelectorRestoreForNextFetch = true;
          this._suppressUrlUpdateForEvent = true;
        }
      }

      _performLocalIncompleteUpdate() {
        this.variantSelectors?.recalculateAvailability?.();
        this.updateVariantInputs(null);

        this.productForm?.toggleSubmitButton(
          true,
          this.getMissingOptionPromptText(),
        );

        publish(PUB_SUB_EVENTS.variantChange, {
          data: { sectionId: this.sectionId, html: null, variant: null },
        });
      }

      _hasMissingSelections(selectedByPosition = {}) {
        const variantSelectors = this.variantSelectors;
        if (!variantSelectors) return false;

        const groups = Array.from(
          variantSelectors.querySelectorAll("[data-option-position]"),
        );
        if (!groups.length) return false;

        return groups.some((group, index) => {
          const fallbackPosition = index + 1;
          const position =
            parseInt(group.dataset.optionPosition || "", 10) ||
            fallbackPosition;

          return !selectedByPosition[position];
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      getMissingOptionPromptText() {
        const variantSelectors = this.variantSelectors;
        if (!variantSelectors) return window.variantStrings.unavailable;

        const groups = Array.from(
          variantSelectors.querySelectorAll("[data-option-position]"),
        );

        let topOptionPosition = null;
        try {
          if (variantSelectors._initialTopOptionPosition) {
            topOptionPosition = parseInt(
              variantSelectors._initialTopOptionPosition,
              10,
            );
          } else {
            const topScript = variantSelectors.querySelector(
              'script[type="application/json"][data-initial-top-option-position]',
            );
            if (topScript?.textContent?.trim()) {
              topOptionPosition = parseInt(
                JSON.parse(topScript.textContent.trim()),
                10,
              );
            }
          }
        } catch (e) {
          topOptionPosition = null;
        }

        const missingGroups = [];

        for (const group of groups) {
          const hasSelectedRadio = !!group.querySelector(
            'input[type="radio"]:checked',
          );

          let hasSelectedOption = false;
          const select = group.querySelector("select");
          if (select) {
            const selectedOption = select.options?.[select.selectedIndex];
            const selectedValue = (
              selectedOption?.value ||
              select.value ||
              ""
            ).toString();
            hasSelectedOption = !!selectedValue;
          }

          if (hasSelectedRadio || hasSelectedOption) continue;

          missingGroups.push(group);
        }

        if (!missingGroups.length) return window.variantStrings.unavailable;

        const preferredMissingGroup =
          missingGroups.find((group) => {
            const pos = parseInt(group.dataset.optionPosition || "", 10);
            return topOptionPosition ? pos !== topOptionPosition : true;
          }) || missingGroups[0];

        if (preferredMissingGroup) {
          const rawLabel =
            preferredMissingGroup.querySelector("legend")?.textContent ||
            preferredMissingGroup.querySelector("label.form__label")
              ?.textContent ||
            "";
          const cleanedLabel = rawLabel
            .replace(/:\s*$/, "")
            .replace(/\s+/g, " ")
            .trim();

          if (cleanedLabel) {
            return `Select a ${cleanedLabel.toLowerCase()}`;
          }
        }

        return window.variantStrings.unavailable;
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            const htmlUpdateUtility = globalThis.HTMLUpdateUtility;
            if (htmlUpdateUtility?.viewTransition) {
              htmlUpdateUtility.viewTransition(
                document.querySelector("main"),
                html.querySelector("main"),
                this.preProcessHtmlCallbacks,
                this.postProcessHtmlCallbacks,
              );
            } else {
              const currentMain = document.querySelector("main");
              const incomingMain = html.querySelector("main");
              if (currentMain && incomingMain) {
                this.preProcessHtmlCallbacks.forEach((callback) =>
                  callback(incomingMain),
                );
                currentMain.innerHTML = incomingMain.innerHTML;
                this.postProcessHtmlCallbacks.forEach((callback) =>
                  callback(currentMain),
                );
              }
            }
          } else {
            const htmlUpdateUtility = globalThis.HTMLUpdateUtility;
            if (htmlUpdateUtility?.viewTransition) {
              htmlUpdateUtility.viewTransition(
                this,
                html.querySelector("product-info"),
                this.preProcessHtmlCallbacks,
                this.postProcessHtmlCallbacks,
              );
            } else {
              const incomingProductInfo = html.querySelector("product-info");
              if (incomingProductInfo) {
                this.preProcessHtmlCallbacks.forEach((callback) =>
                  callback(incomingProductInfo),
                );
                this.innerHTML = incomingProductInfo.innerHTML;
                this.postProcessHtmlCallbacks.forEach((callback) =>
                  callback(this),
                );
              }
            }
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            callback(html);
          })
          .then(() => {
            // Keep focus stable. Only move focus when caller provides a targetId
            // (e.g., user clicked a variant option). Avoid focusing on initial load
            // to prevent mobile scroll-jumps.
            if (targetId) document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        // Prefer a script[type="application/json"][data-selected-variant] inside variant-selects
        const script = productInfoNode.querySelector(
          'variant-selects script[type="application/json"][data-selected-variant]',
        );

        // Helper: normalize parsed object keys (trim) and fix title casing if needed
        const normalizeResult = (obj) => {
          if (!obj) return null;
          const out = {};
          Object.keys(obj).forEach((k) => {
            out[k.trim()] = obj[k];
          });
          if (
            typeof out.title === "string" &&
            out.title === out.title.toLowerCase()
          ) {
            out.title = out.title.charAt(0).toUpperCase() + out.title.slice(1);
          }
          return out;
        };

        // If script exists, use its textContent (safe JSON embedding)
        if (script && script.textContent && script.textContent.trim()) {
          try {
            return normalizeResult(JSON.parse(script.textContent.trim()));
          } catch (e) {
            console.warn(
              "product-info: failed to parse selected-variant JSON from script",
              e,
            );
            return null;
          }
        }

        // Fallback: find any element with data-selected-variant and try to parse its textContent/attribute
        const node = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]",
        );
        if (!node) return null;
        const payload = (
          node.textContent ||
          node.getAttribute("data-selected-variant") ||
          ""
        ).trim();
        if (!payload) return null;
        try {
          return normalizeResult(JSON.parse(payload));
        } catch (e) {
          // Fallback: try to extract a JSON-like {...} substring from the
          // surrounding HTML (handles test fixtures where attributes were
          // split by the HTML parser).
          try {
            const outer =
              productInfoNode.innerHTML || productInfoNode.outerHTML || "";
            const braceMatch = outer.match(/\{[\s\S]*?\}/);
            if (braceMatch && braceMatch[0])
              return normalizeResult(JSON.parse(braceMatch[0]));
          } catch (e2) {
            /* ignore */
          }
          console.warn(
            "product-info: failed to parse selected-variant JSON",
            e,
          );
          return null;
        }
      }

      // Read all variants JSON embedded in the variant-selects block, falling
      // back to window.productVariants if present. Returns an array of
      // variant objects (may be empty).
      getAllVariants() {
        try {
          const vs = this.variantSelectors;
          if (vs) {
            const script = vs.querySelector(
              'script[type="application/json"][data-variants]',
            );
            if (script && script.textContent && script.textContent.trim()) {
              return JSON.parse(script.textContent.trim());
            }
          }
        } catch (e) {
          /* ignore parse errors */
        }

        try {
          // Older pages may still expose a global mapping; use it as a fallback
          const w =
            window.productVariants?.[parseInt(this.dataset.productId, 10)];
          if (Array.isArray(w)) return w;
        } catch (e) {
          /* ignore */
        }

        return [];
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false,
        selectedByPositionOverride = null,
      ) {
        const params = [];

        if (!shouldFetchFullPage) params.push(`section_id=${this.sectionId}`);

        if (optionValues && optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        // If variant selectors match a known variant, add variant param for convenience
        try {
          const vs = this.variantSelectors;
          if (vs) {
            const selectedByPosition =
              selectedByPositionOverride &&
              Object.keys(selectedByPositionOverride).length
                ? selectedByPositionOverride
                : this.getSelectedOptionsByPosition(vs);
            const pv = this.getAllVariants();
            const match = this.findVariantBySelectedOptions(
              pv,
              selectedByPosition,
            );
            if (match?.id) params.push(`variant=${String(match.id)}`);
          }
        } catch (e) {
          /* best-effort: ignore selector parsing failures */
        }

        const qs = params.join("&");
        return `${url}${qs ? `?${qs}` : ""}`;
      }

      getSelectedOptionsByPosition(root = this.variantSelectors) {
        const selectedByPosition = {};
        if (!root?.querySelectorAll) return selectedByPosition;

        const selectedElements = Array.from(
          root.querySelectorAll(
            "select option[selected], fieldset input:checked",
          ),
        );

        if (selectedElements.length) {
          selectedElements.forEach((el, idx) => {
            const position =
              parseInt(el.dataset.optionPosition || "", 10) || idx + 1;
            const value = (el.value || el.textContent || "").toString();
            if (value) selectedByPosition[position] = value;
          });

          return selectedByPosition;
        }

        const selects = Array.from(root.querySelectorAll("select"));
        if (selects.length) {
          selects.forEach((select, idx) => {
            const selectedOption = select.options?.[select.selectedIndex];
            const position =
              parseInt(selectedOption?.dataset?.optionPosition || "", 10) ||
              parseInt(
                select.closest("[data-option-position]")?.dataset
                  ?.optionPosition || "",
                10,
              ) ||
              idx + 1;
            const value = (
              selectedOption?.value ||
              select.value ||
              selectedOption?.textContent ||
              ""
            ).toString();
            if (value) selectedByPosition[position] = value;
          });

          Array.from(
            root.querySelectorAll(
              "fieldset input:checked, input[type='radio']:checked",
            ),
          ).forEach((input, idx) => {
            const position =
              parseInt(input.dataset.optionPosition || "", 10) ||
              parseInt(
                input.closest("[data-option-position]")?.dataset
                  ?.optionPosition || "",
                10,
              ) ||
              idx + 1;
            const value = (input.value || "").toString();
            if (value) selectedByPosition[position] = value;
          });

          return selectedByPosition;
        }

        const groups = Array.from(
          root.querySelectorAll("[data-option-position]"),
        );
        groups.forEach((group, idx) => {
          const position =
            parseInt(group.dataset.optionPosition || "", 10) || idx + 1;
          const opt = group.querySelector("select option[selected]");
          if (opt) {
            const value = (opt.value || opt.textContent || "").toString();
            if (value) selectedByPosition[position] = value;
            return;
          }
          const radio = group.querySelector("input:checked");
          if (radio) {
            const value = (radio.value || "").toString();
            if (value) selectedByPosition[position] = value;
          }
        });

        return selectedByPosition;
      }

      findVariantBySelectedOptions(variants, selectedByPosition = {}) {
        if (!Array.isArray(variants) || !Object.keys(selectedByPosition).length)
          return null;

        return variants.find((variant) => {
          for (const [positionKey, selectedValue] of Object.entries(
            selectedByPosition,
          )) {
            const position = parseInt(positionKey, 10);
            if (!selectedValue) continue;
            if (
              String(variant[`option${position}`] || "") !==
              String(selectedValue)
            ) {
              return false;
            }
          }
          return true;
        });
      }

      updateOptionValues(html) {
        const sourceRoot = html.querySelector("variant-selects");
        const targetRoot = this.variantSelectors;

        if (!sourceRoot || !targetRoot) return;

        const currentSelectedIds =
          targetRoot?.selectedOptionValues?.slice?.() || [];

        /**
         * ------------------------------------------------------------------
         * MERGE PATH
         * ------------------------------------------------------------------
         * When suppression for the next fetch is active, avoid replacing the
         * entire <variant-selects> DOM.
         *
         * Instead, merge updated JSON payloads (data-variants,
         * data-selected-variant, data-initial-top-option-position) into the
         * existing DOM. This prevents overwriting an explicit user click
         * that may have occurred while the fetch was in-flight.
         */
        if (this._suppressVariantSelectorRestoreForNextFetch) {
          try {
            this._mergeJsonScripts(sourceRoot, targetRoot);
            this._copyRootAttributes(sourceRoot, targetRoot);

            // Recalculate availability based on the newly merged variants JSON
            targetRoot?.recalculateAvailability?.();

            // Clear suppression flags and request bookkeeping
            this._suppressVariantSelectorRestoreForNextFetch = false;
            this._lastRequestedOptionValues = null;
          } catch (error) {
            // If merge-path fails, allow fallback to full replacement
            console.warn(
              "product-info: merge-path failed, falling back",
              error,
            );
          }

          return;
        }

        /**
         * ------------------------------------------------------------------
         * FULL REPLACEMENT PATH
         * ------------------------------------------------------------------
         * Replace the entire variant-selects block.
         */
        const htmlUpdateUtility = globalThis.HTMLUpdateUtility;
        if (htmlUpdateUtility?.setInnerHTML) {
          htmlUpdateUtility.setInnerHTML(targetRoot, sourceRoot.innerHTML);
        } else {
          targetRoot.innerHTML = sourceRoot.innerHTML;
        }

        /**
         * If suppression is requested (initial server-chosen default),
         * do NOT restore prior client selections.
         *
         * Instead, refresh metadata on server-selected inputs so swatches
         * and selected-value displays reflect the server state.
         */
        if (this._suppressVariantSelectorRestore) {
          try {
            const serverSelected = Array.from(
              targetRoot.querySelectorAll(
                "select option[selected], fieldset input:checked",
              ),
            );

            serverSelected.forEach((el) =>
              targetRoot.updateSelectionMetadata({ target: el }),
            );

            // Ensure disabled / availability UI reflects server state
            targetRoot?.recalculateAvailability?.();
          } catch {
            /* best-effort UI refresh */
          } finally {
            // Suppress only once (initial hydration)
            this._suppressVariantSelectorRestore = false;
          }

          return;
        }

        /**
         * ------------------------------------------------------------------
         * DEFAULT RESTORE PATH
         * ------------------------------------------------------------------
         * After replacement, attempt to restore the client's prior selection.
         */
        this._restoreClientSelections(targetRoot, currentSelectedIds);

        // Ensure availability is consistent after restoring metadata
        targetRoot?.recalculateAvailability?.();
      }

      _mergeJsonScripts(sourceRoot, targetRoot) {
        const JSON_SELECTORS = [
          'script[type="application/json"][data-variants]',
          'script[type="application/json"][data-selected-variant]',
          'script[type="application/json"][data-initial-top-option-position]',
        ];

        JSON_SELECTORS.forEach((selector) => {
          const src = sourceRoot.querySelector(selector);
          if (!src?.textContent) return;

          const dst = targetRoot.querySelector(selector);

          if (dst) {
            dst.textContent = src.textContent;
          } else {
            targetRoot.appendChild(src.cloneNode(true));
          }
        });
      }

      _copyRootAttributes(sourceRoot, targetRoot) {
        try {
          for (const { name, value } of Array.from(
            sourceRoot.attributes || [],
          )) {
            if (!name || !value) continue;

            /**
             * Intentionally permissive:
             * We copy inline attributes from the server-rendered block.
             * If needed, this can be tightened to avoid overwriting
             * client-only dataset attributes.
             */
            targetRoot.setAttribute(name, value);
          }
        } catch {
          /* best-effort */
        }
      }

      _restoreClientSelections(targetRoot, selectedIds) {
        selectedIds.forEach((id) => {
          if (!id) return;

          const el = targetRoot.querySelector(`[data-option-value-id="${id}"]`);
          if (!el) return;

          const tag = el.tagName;

          if (tag === "OPTION") {
            const select = el.closest("select");
            if (select) {
              select.value = el.value;
              el.setAttribute("selected", "selected");
            }
          } else if (tag === "INPUT" && el.type === "radio") {
            const name = el.getAttribute("name").replace(/(\r\n|\n|\r)/g, "");

            targetRoot
              .querySelectorAll(`input[type="radio"][name="${name}"]`)
              .forEach((r) => (r.checked = false));

            el.checked = true;
          }

          try {
            targetRoot.updateSelectionMetadata({ target: el });
          } catch {
            /* best-effort UI refresh */
          }
        });
      }

      showDiscountedPriceFromBodyTag() {
        const useDynamicDiscount = document.body.getAttribute(
          "data-dynamic-discount",
        );
        const discountCode = document.body.getAttribute("data-discount-size");
        const googleDiscountCode = new URLSearchParams(
          window.location.search,
        ).get("pv2");

        if (!(useDynamicDiscount && discountCode)) return;

        // if the cart meta data has this variant id, suppress
        const hiddenField = document.getElementById("cart_metadata")?.value;
        if (hiddenField) {
          let variant = this.getSelectedVariant(
            document.querySelector("product-info[id^='MainProduct']"),
          );
          if (variant && variant.id) {
            if (hiddenField.includes(variant.id.toString())) return;
          }
          variant = this.getSelectedVariant(
            document.querySelector("product-info"),
          );
          if (variant && variant.id) {
            if (hiddenField.includes(variant.id.toString())) return;
          }
        }

        const priceContainers = [
          this.querySelector(`#price-${this.dataset.section}`),
          this.querySelector(`#mobile-price-${this.dataset.section}`),
        ].filter(Boolean);

        priceContainers.forEach((container) => {
          const discountPriceSpans = Array.from(
            container.querySelectorAll(".discount_code_mapping_price"),
          );
          const regularPrice = container.querySelector(".price__regular");
          const salePrice = container.querySelector(".price__sale");
          const message = container.querySelector(
            ".meta-discount-price-message",
          );

          discountPriceSpans.forEach((item) => {
            item.style.setProperty("display", "none", "important");
          });

          let discountSet = false;
          const discountPriceSpan = discountPriceSpans.find((item) => {
            const itemCode = (item.dataset.discount || "").trim().toUpperCase();
            const wanted = String(discountCode || "").trim().toUpperCase();
            return itemCode === wanted;
          });

          if (discountPriceSpan && !googleDiscountCode) {
            discountPriceSpan.style.setProperty(
              "display",
              "initial",
              "important",
            );
            regularPrice?.style.setProperty("display", "none", "important");
            salePrice?.style.setProperty("display", "none", "important");
            discountSet = true;
          } else {
            regularPrice?.style.removeProperty("display");
            salePrice?.style.removeProperty("display");
          }

          if (discountSet) {
            message?.classList.remove("visually-hidden");
          } else {
            message?.classList.add("visually-hidden");
          }
        });
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          if (this._suppressInitialUrlUpdate) {
            // Only skip the URL update for the initial hydration fetch;
            // reset the flag so subsequent updates will update the URL.
            this._suppressInitialUrlUpdate = false;
          } else if (this._suppressUrlUpdateForEvent) {
            // For user-driven flows, update share-button only and avoid
            // touching the browser history.
            try {
              this.querySelector("share-button")?.updateUrl(
                `${window.shopUrl}${productUrl}${variant?.id ? `?variant=${variant.id}` : ""}`,
              );
            } catch (e) {
              /* best-effort */
            }
            // Reset the flag after honoring it once
            this._suppressUrlUpdateForEvent = false;
          } else {
            this.updateURL(productUrl, variant?.id);
          }
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false,
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`,
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden"),
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === "",
          );
          updateSourceFromDestination(
            "Low-Inventory",
            ({ innerText }) => innerText === "",
          );
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden"),
          );

          this.updateLeadTime(
            variant.available,
            window.variantMetafields[variant.id].lead_time,
            window.variantMetafields[variant.id].qohlocal,
            window.variantMetafields[variant.id].jitflag,
            window.variantMetafields[variant.id].vendor,
          );
          this.updateCustomField(
            "Discountable",
            window.variantMetafields[variant.id].discountable,
          );
          this.updateCustomField(
            "Extra-Ship",
            window.variantMetafields[variant.id].extraship,
          );
          this.updateCustomField(
            "promo_message",
            window.variantMetafields[variant.id].promo_message,
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`,
          )?.classList.remove("hidden");
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`,
          )?.classList.remove("hidden");

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("aria-disabled") ?? true,
            window.variantStrings.soldOut,
          );

          this.showDiscountedPriceFromBodyTag();
          this.updateCollapsibleTabs(variant.id);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateLeadTime(available, days, localQty, jitFlag, vendor) {
        const field = document.getElementById(
          `Lead-Time-${this.dataset.section}`,
        );
        const vendorNames =
          "HORSEWARE,BRADLEY CALDWELL,ERS,HORZE,IRH,INTREPID,JT INTERNATIONAL,JPC,MUSTANG,OUTBACK,PERRI'S,SADDLERY BRANDS,SHIRES,WEAVER,WEATHERBEETA,DEVON AIRE,GARMIN,BOLLMAN";

        if (!field) {
          return;
        }

        if (localQty && localQty > 0) {
          days = 1;
          jitFlag = "N";
        }

        if (!vendorNames.includes(vendor.toUpperCase()) && localQty < 1) {
          jitFlag = "DS";
        }

        if (!days || !available) {
          field.parentNode.classList.add("visually-hidden");
          return;
        }

        days = parseInt(days);

        if (days == 1 || localQty > 0) {
          days = `1 business day`;
        } else if (days == 2) {
          days = `1-2 business days`;
        } else if (days == 3) {
          days = `1-3 business days`;
        } else if (days == 4) {
          days = `2-4 business days`;
        } else if (days == 5) {
          days = `3-5 business days`;
        } else if (days == 6) {
          days = `4-6 business days`;
        } else if (days == 7) {
          days = `5-7 business days`;
        } else if (days >= 8 && days <= 14) {
          days = `1-2 weeks`;
        } else if (days >= 15 && days <= 21) {
          days = `2-3 weeks`;
        } else if (days >= 22 && days <= 18) {
          days = `3-4 weeks`;
        } else if (days >= 29) {
          days = `1-2 months`;
        }

        field.parentNode.classList.remove("visually-hidden");
        if (days != true) {
          field.textContent = "Usually Ships in " + days ?? "";
        }
      }

      updateCustomField(name, value) {
        const field = document.getElementById(
          name + `-${this.dataset.section}`,
        );

        if (!field) {
          return;
        }

        if (!value || value == "$0.00") {
          field.parentNode.classList.add("visually-hidden");
          return;
        }

        field.parentNode.classList.remove("visually-hidden");
        if (value != true) {
          if (this.containsHtml(value)) {
            field.style.display = "block";
            field.innerHTML = value ?? "";
          } else {
            field.textContent = value ?? "";
          }
        }
      }

      containsHtml(value) {
        if (typeof value !== "string") return false;

        // Fast-path: no "<" at all → definitely plain text
        if (!value.includes("<")) return false;

        // Regex: < followed by a tag name (letters, numbers, hyphens, underscores)
        //        then optional attributes, then > or />
        //        Also catches self-closing tags and comments.
        const htmlTagRE = /<\s*(?:[a-z0-9_-]+|!--)/i;

        return htmlTagRE.test(value);
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        // Preserve tracking / campaign params (e.g., utm_*, trk_*) from current URL
        const preserveParamPatterns = [/^utm_/, /^trk_/];

        // Build the target URL object (works for relative or absolute urls)
        let target;
        try {
          target = new URL(url, window.location.origin);
        } catch (e) {
          target = new URL(window.location.origin + url);
        }

        // Ensure variant param is set/removed on the target URL
        if (variantId) {
          target.searchParams.set("variant", String(variantId));
        } else {
          target.searchParams.delete("variant");
        }

        // Merge preserved params from current location without overwriting existing ones
        try {
          const currentParams = new URLSearchParams(window.location.search);
          for (const [k, v] of currentParams.entries()) {
            if (
              preserveParamPatterns.some((re) => re.test(k)) &&
              !target.searchParams.has(k)
            ) {
              target.searchParams.set(k, v);
            }
          }
        } catch (e) {
          /* ignore parse errors */
        }

        const relativeUrl = `${target.pathname}${target.search}${target.hash}`;

        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${relativeUrl}`,
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState({}, "", relativeUrl);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable,
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Price-Per-Item",
          "Volume-Note",
          "Volume",
          "Quantity-Rules",
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector("media-gallery ul");
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll("li[data-media-id]"),
          );
          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId),
          );
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll("li[data-media-id]"),
          );

          let shouldRefresh = false;
          // For media items that exist in both source and destination, ensure
          // the source item's innerHTML is updated so dynamic pieces like
          // badges reflect the server-rendered variant-specific markup.
          mediaGalleryDestinationItems.forEach((destinationItem) => {
            const mid = destinationItem.dataset.mediaId;
            const sourceData = sourceMap.get(mid);
            if (
              sourceData &&
              sourceData.item &&
              sourceData.item.innerHTML !== destinationItem.innerHTML
            ) {
              sourceData.item.innerHTML = destinationItem.innerHTML;
              shouldRefresh = true;
            }
          });
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId),
          );

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)
            ) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh)
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`,
                  ),
                );

                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            },
          );
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(
          `.product-media-modal__content`,
        );
        const newModalContent = html.querySelector(
          `product-modal .product-media-modal__content`,
        );
        if (modalContent && newModalContent)
          modalContent.innerHTML = newModalContent.innerHTML;
      }

      /**
       * Updates any collapsible tabs that are visible and have a variant metafield
       * @param {*} variantId Selected variant id
       */
      updateCollapsibleTabs(variantId) {
        this.querySelectorAll('[data-variant-metafield="true"]').forEach(
          (tab) => {
            // get config data from tab's dataset
            const key = tab.dataset.variantMetafieldKey;
            const ns = tab.dataset.variantMetafieldNamespace ?? "custom";
            const value = window.variantMetafields[variantId]?.[ns]?.[key];
            const expected = tab.dataset.variantMetafieldValue;
            if (!key || !value) return;
            // booleans will automatically be strings on both sides
            if (value.toLowerCase() != expected.toLowerCase()) {
              tab.style.display = "none";
              tab.classList.add("hidden");
            } else {
              tab.style.display = "block";
              tab.classList.remove("hidden");
            }
          },
        );
      }

      setQuantityBoundries() {
        //const currentValue = parseInt(this.quantityInput.value, 10);
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }

        /*let nextValue = Number.isFinite(currentValue) ? currentValue : min;
        if (nextValue < min) nextValue = min;
        if (max !== null && nextValue > max) nextValue = max;
        this.quantityInput.value = nextValue;*/

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner",
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`,
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner",
            ).classList.add("hidden"),
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`,
        );
        //if (!quantityFormUpdated) return;
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }

        //this.setQuantityBoundries();
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products",
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`,
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list",
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`,
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    },
  );
}